import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc, addDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import './EstimateApproval.css';

/**
 * Public-facing estimate approval page.
 * Route: /estimate/:token
 *
 * Client lands here from the link in the "Estimate Sent" email.
 * No login required — the token in the URL is the auth.
 *
 * Flow:
 *  - On mount, query workOrders where approvalToken == :token (limit 1)
 *  - Display the estimate
 *  - Approve/Decline buttons capture signature + timestamp + decision
 *  - On approve → workOrder status flips to 'Approved'
 *  - On decline → workOrder status flips to 'Declined', reason stored
 *  - Cloud function triggers send pager email to admin
 */

export default function EstimateApproval() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [workOrder, setWorkOrder] = useState(null);
  const [error, setError] = useState('');

  // Decision form state
  const [decision, setDecision] = useState(null); // null | 'approve' | 'decline'
  const [signatureName, setSignatureName] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (!token) {
      setError('Invalid estimate link.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const q = query(collection(db, 'workOrders'), where('approvalToken', '==', token));
        const snap = await getDocs(q);
        if (cancelled) return;
        if (snap.empty) {
          setError('We couldn\u2019t find this estimate. The link may be expired or invalid.');
          setLoading(false);
          return;
        }
        const woDoc = snap.docs[0];
        const wo = { id: woDoc.id, ...woDoc.data() };
        setWorkOrder(wo);
        setSignatureName(wo.clientName || wo.contactName || '');
        if (wo.estimateDecision === 'approved' || wo.estimateDecision === 'declined') {
          setSubmitted(true);
        }
      } catch (err) {
        console.error('Error loading estimate', err);
        setError('Something went wrong loading this estimate. Please try again or call us at (516) 386-9923.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async () => {
    if (!decision) return;
    if (!signatureName.trim()) {
      alert('Please type your name to confirm.');
      return;
    }
    setSubmitting(true);
    try {
      const newStatus = decision === 'approve' ? 'Approved' : 'Declined';
      const updateData = {
        status: newStatus,
        estimateDecision: decision === 'approve' ? 'approved' : 'declined',
        estimateDecisionAt: serverTimestamp(),
        estimateSignatureName: signatureName.trim(),
        ...(decision === 'decline' && { estimateDeclineReason: declineReason.trim() || null }),
      };
      await updateDoc(doc(db, 'workOrders', workOrder.id), updateData);

      // Also write a record to a separate collection that Cloud Function watches
      await addDoc(collection(db, 'estimateDecisions'), {
        workOrderId: workOrder.id,
        woNumber: workOrder.woNumber || null,
        propertyId: workOrder.propertyId || null,
        clientName: workOrder.clientName || workOrder.contactName || '',
        clientEmail: workOrder.clientEmail || workOrder.contactEmail || '',
        title: workOrder.title || '',
        estimateTotal: workOrder.estimate?.total ?? null,
        decision: decision === 'approve' ? 'approved' : 'declined',
        signatureName: signatureName.trim(),
        declineReason: decision === 'decline' ? (declineReason.trim() || null) : null,
        createdAt: serverTimestamp(),
      });

      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Submission failed', err);
      alert('Something went wrong submitting your decision. Please try again or call us at (516) 386-9923.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="rh-est-page">
        <div className="rh-est-loading">Loading your estimate...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rh-est-page">
        <div className="rh-est-card">
          <div className="rh-est-error">
            <h1>Something\u2019s not right</h1>
            <p>{error}</p>
            <p>If you think this is a mistake, please call us at <a href="tel:+15163869923">(516) 386-9923</a>.</p>
            <Link to="/" className="rh-est-btn rh-est-btn-secondary">Back to home</Link>
          </div>
        </div>
      </div>
    );
  }

  const est = workOrder.estimate || {};
  const hours = est.hours || 0;
  const rate = est.rate || 120;
  const materials = est.materials || 0;
  const total = est.total || (hours * rate + materials);
  const laborTotal = hours * rate;

  // Already-decided view
  if (submitted) {
    const wasApproved = (workOrder.estimateDecision === 'approved') || decision === 'approve';
    return (
      <div className="rh-est-page">
        <div className="rh-est-card">
          <div className="rh-est-success">
            <div className="rh-est-success-icon">
              {wasApproved ? (
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="11" fill="#EAF7F0" />
                  <path d="M7 12.5l3.5 3.5L17 9" stroke="#1D6B45" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="11" fill="#F5F2EC" />
                  <path d="M8 8l8 8M16 8l-8 8" stroke="#6B6559" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
              )}
            </div>
            <h1>{wasApproved ? 'Estimate approved' : 'Estimate declined'}</h1>
            <p>
              {wasApproved
                ? `Thanks for the approval. We'll reach out within one business day to schedule the work. If anything is urgent in the meantime, give us a call.`
                : `Got it — thanks for letting us know. If you'd like to discuss any part of the estimate or have us put together a different scope, give us a call anytime.`}
            </p>
            <div className="rh-est-success-meta">
              <div><strong>Project:</strong> {workOrder.title}</div>
              <div><strong>Total:</strong> ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <p style={{ marginTop: 32 }}>
              Questions? <a href="tel:+15163869923">(516) 386-9923</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Approval form
  return (
    <div className="rh-est-page">
      <div className="rh-est-card">

        {/* Header */}
        <div className="rh-est-header">
          <div className="rh-est-brand">
            <span className="rh-est-brand-serif">Reliable</span>
            <span className="rh-est-brand-bold">HANDYMAN</span>
          </div>
          <div className="rh-est-meta">
            <div className="rh-est-meta-label">Estimate</div>
            <div className="rh-est-meta-value">{workOrder.woNumber || workOrder.id.slice(0, 8).toUpperCase()}</div>
          </div>
        </div>

        {/* Greeting */}
        <div className="rh-est-greeting">
          <h1>Estimate for {(workOrder.clientName || workOrder.contactName || 'your project').split(' ')[0]}</h1>
          <p className="rh-est-address">
            {workOrder.contactAddress || ''}
            {workOrder.propertyId && <span className="rh-est-pid"> · {workOrder.propertyId}</span>}
          </p>
        </div>

        {/* Project */}
        <div className="rh-est-section">
          <div className="rh-est-section-label">Project</div>
          <h2 className="rh-est-section-title">{workOrder.title}</h2>
          {workOrder.description && (
            <p className="rh-est-section-body">{workOrder.description}</p>
          )}
        </div>

        {/* Line items */}
        <div className="rh-est-section">
          <div className="rh-est-section-label">Estimate breakdown</div>
          <div className="rh-est-lines">
            <div className="rh-est-line">
              <div className="rh-est-line-desc">
                <div className="rh-est-line-title">Labor</div>
                <div className="rh-est-line-sub">{hours} hr{hours !== 1 ? 's' : ''} \u00d7 ${rate}/hr</div>
              </div>
              <div className="rh-est-line-amount">${laborTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            {materials > 0 && (
              <div className="rh-est-line">
                <div className="rh-est-line-desc">
                  <div className="rh-est-line-title">Materials estimate</div>
                  <div className="rh-est-line-sub">At cost, with receipts</div>
                </div>
                <div className="rh-est-line-amount">${materials.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </div>
            )}
            <div className="rh-est-line rh-est-line-total">
              <div className="rh-est-line-desc">
                <div className="rh-est-line-title">Total</div>
              </div>
              <div className="rh-est-line-amount">${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          </div>

          {est.notes && (
            <div className="rh-est-notes">
              <div className="rh-est-notes-label">Notes from your estimator</div>
              <div className="rh-est-notes-body">{est.notes}</div>
            </div>
          )}
        </div>

        {/* Terms */}
        <div className="rh-est-section">
          <div className="rh-est-section-label">Terms</div>
          <ul className="rh-est-terms">
            <li>This estimate is valid for 30 days from when it was sent.</li>
            <li>Materials are billed at cost \u2014 you\u2019ll receive itemized receipts after the work is complete.</li>
            <li>Labor has a 1-hour minimum, then is billed in whole-hour blocks. No trip charges.</li>
            <li>1-year workmanship guarantee on all labor. If something we fix breaks within a year, we come back at no charge.</li>
            <li>Payment is due within 5 days of job completion.</li>
            <li>Reliable Handyman is licensed and insured in New York.</li>
          </ul>
        </div>

        {/* Decision */}
        <div className="rh-est-section rh-est-decision">
          <div className="rh-est-section-label">Your decision</div>

          {!decision && (
            <div className="rh-est-decision-buttons">
              <button className="rh-est-btn rh-est-btn-primary" onClick={() => setDecision('approve')}>
                \u2713 Approve estimate
              </button>
              <button className="rh-est-btn rh-est-btn-secondary" onClick={() => setDecision('decline')}>
                Decline
              </button>
            </div>
          )}

          {decision === 'approve' && (
            <div className="rh-est-confirm">
              <p>
                By typing your name below and clicking <strong>Confirm Approval</strong>, you agree to the scope and pricing shown above and authorize us to proceed with the work. We\u2019ll reach out within one business day to schedule.
              </p>
              <label className="rh-est-label">Type your full name to confirm</label>
              <input
                type="text"
                className="rh-est-input"
                value={signatureName}
                onChange={e => setSignatureName(e.target.value)}
                placeholder="Jane Smith"
                autoFocus
              />
              <div className="rh-est-confirm-buttons">
                <button className="rh-est-btn rh-est-btn-secondary" onClick={() => setDecision(null)} disabled={submitting}>Back</button>
                <button className="rh-est-btn rh-est-btn-primary" onClick={handleSubmit} disabled={submitting || !signatureName.trim()}>
                  {submitting ? 'Submitting...' : 'Confirm Approval'}
                </button>
              </div>
            </div>
          )}

          {decision === 'decline' && (
            <div className="rh-est-confirm">
              <p>
                We\u2019re sorry this didn\u2019t work out. If you have a moment, sharing why helps us improve. Either way, no hard feelings \u2014 give us a call anytime if your needs change.
              </p>
              <label className="rh-est-label">Type your name</label>
              <input
                type="text"
                className="rh-est-input"
                value={signatureName}
                onChange={e => setSignatureName(e.target.value)}
                placeholder="Jane Smith"
                autoFocus
              />
              <label className="rh-est-label">Reason (optional)</label>
              <textarea
                className="rh-est-textarea"
                value={declineReason}
                onChange={e => setDeclineReason(e.target.value)}
                placeholder="Pricing, timing, scope, or anything else..."
                rows={3}
              />
              <div className="rh-est-confirm-buttons">
                <button className="rh-est-btn rh-est-btn-secondary" onClick={() => setDecision(null)} disabled={submitting}>Back</button>
                <button className="rh-est-btn rh-est-btn-decline" onClick={handleSubmit} disabled={submitting || !signatureName.trim()}>
                  {submitting ? 'Submitting...' : 'Confirm Decline'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="rh-est-footer">
          Questions? Call us at <a href="tel:+15163869923">(516) 386-9923</a> or reply to the email you received.
        </div>

      </div>
    </div>
  );
}
